import type { BuilderStudioBlockSelection } from "./types";

export type BuilderStudioFloatingPointerContext = {
  insideCanvas: boolean;
  insideFloatingToolbar: boolean;
};

/**
 * Identifies the concrete canvas selection that owns floating editor chrome.
 *
 * UUID-backed blocks keep the same key when their document path changes.
 * Runtime targets are part of the identity because one reusable prototype can
 * render several independently selectable products or media values.
 */
export function builderStudioFloatingSelectionKey(
  selection: BuilderStudioBlockSelection | null,
): string | null {
  if (!selection) {
    return null;
  }

  const blockIdentity = selection.blockUuid
    ? ["uuid", selection.blockUuid]
    : selection.blockPath
      ? ["path", selection.blockPath]
      : ["root", selection.rootIndex];
  const runtimeTarget = selection.runtimeTarget
    ? [
        selection.runtimeTarget.kind,
        selection.runtimeTarget.entityType,
        selection.runtimeTarget.entityUuid,
        selection.runtimeTarget.context ?? null,
        selection.runtimeTarget.valueUuid ?? null,
      ]
    : null;

  return JSON.stringify([
    blockIdentity,
    selection.part ?? null,
    selection.slot ?? null,
    runtimeTarget,
  ]);
}

/**
 * Canvas interactions and interactions inside the active toolbar retain its
 * visibility. Moving into surrounding studio chrome dismisses the floating
 * layer without prescribing whether the controlled block selection is kept.
 */
export function builderStudioShouldDismissFloatingTools({
  insideCanvas,
  insideFloatingToolbar,
}: BuilderStudioFloatingPointerContext): boolean {
  return !insideCanvas && !insideFloatingToolbar;
}

/**
 * Releases only the failed in-flight endpoint value. A newer commit may have
 * replaced it while the older request was pending and must stay deduplicated.
 */
export function builderStudioRuntimeEndpointCommitAfterFailure(
  currentCommitKey: string | null,
  failedCommitKey: string,
): string | null {
  return currentCommitKey === failedCommitKey ? null : currentCommitKey;
}
